import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import LeadConsultationFollowUpPerformanceReportApi from "../../../../../infrastructure/api/leadConsultationFollowUpPerformanceReport";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliDate, toMiladyDate } from "../../../../middleware/middleware";

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { fromDate: toMiladyDate(from), toDate: toMiladyDate(to) };
};

const initialValues = {
  ...getDefaultDateRange(),
  userId: "",
  source: "0",
};

/**
 * Manager report: expert follow-up KPIs on consultation requests
 * and customers without project request.
 */
const LeadConsultationFollowUpPerformanceReportPage = () => {
  const pageInfo = useGetPageInfo();
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(true);
  const [filterValues, setFilterValues] = useState(initialValues);
  const [filterOptions, setFilterOptions] = useState({
    experts: [],
    sources: [],
  });
  const [rows, setRows] = useState([]);

  const headCells = useMemo(
    () => [
      { id: "fullName", label: "کارشناس" },
      { id: "sourceTitle", label: "گروه مشتری" },
      { id: "totalFollowUps", label: "کل پیگیری‌ها", type: "number" },
      { id: "callCount", label: "تماس", type: "number" },
      { id: "needsFollowUpCount", label: "نیازمند پیگیری", type: "number" },
      {
        id: "overdueFollowUpCountDisplay",
        label: "پیگیری عقب‌افتاده",
        type: "chip",
        model: { colorField: "overdueChipColor" },
      },
      {
        id: "convertedCount",
        label: "نتیجه مثبت / تبدیل",
        type: "number",
      },
      { id: "lastFollowUpAtDisplay", label: "آخرین پیگیری" },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        rowKey: `${row.userId || row.UserId}-${row.source ?? row.Source}`,
        fullName: row.fullName || row.FullName,
        sourceTitle: row.sourceTitle || row.SourceTitle,
        totalFollowUps: row.totalFollowUps ?? row.TotalFollowUps ?? 0,
        callCount: row.callCount ?? row.CallCount ?? 0,
        needsFollowUpCount:
          row.needsFollowUpCount ?? row.NeedsFollowUpCount ?? 0,
        convertedCount: row.convertedCount ?? row.ConvertedCount ?? 0,
        overdueFollowUpCount:
          row.overdueFollowUpCount ?? row.OverdueFollowUpCount ?? 0,
        lastFollowUpAtDisplay: (row.lastFollowUpAt || row.LastFollowUpAt)
          ? toJalaliDate(row.lastFollowUpAt || row.LastFollowUpAt)
          : "-",
        overdueFollowUpCountDisplay: String(
          row.overdueFollowUpCount ?? row.OverdueFollowUpCount ?? 0
        ),
        overdueChipColor:
          (row.overdueFollowUpCount ?? row.OverdueFollowUpCount ?? 0) > 0
            ? "error"
            : "gray",
      })),
    [rows]
  );

  useEffect(() => {
    const loadFilters = async () => {
      try {
        setFilterLoading(true);
        const response =
          await LeadConsultationFollowUpPerformanceReportApi.getFilterData();
        if (response?.isSuccess) {
          setFilterOptions({
            experts: response.data?.experts || response.data?.Experts || [],
            sources: response.data?.sources || response.data?.Sources || [],
          });
        }
      } catch (_error) {
        toast.error("خطا در دریافت فیلترهای گزارش");
      } finally {
        setFilterLoading(false);
      }
    };
    loadFilters();
  }, []);

  const onClearFilterValues = () => {
    setFilterValues(initialValues);
    setRows([]);
  };

  const buildSearchPayload = () => ({
    fromDate: filterValues.fromDate || null,
    toDate: filterValues.toDate || null,
    userId: filterValues.userId ? Number(filterValues.userId) : null,
    source: filterValues.source !== "" ? Number(filterValues.source) : 0,
  });

  const onGetReportData = async () => {
    try {
      setLoading(true);
      const response =
        await LeadConsultationFollowUpPerformanceReportApi.getReport(
          buildSearchPayload()
        );
      if (response?.isSuccess) {
        setRows(response.data || []);
        if (!response.data?.length) {
          toast.info("داده‌ای برای نمایش یافت نشد");
        }
      } else {
        toast.error(response?.message || "خطا در دریافت گزارش");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "خطا در دریافت گزارش عملکرد پیگیری لید و مشاوره"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={
              pageInfo?.title ||
              "گزارش عملکرد پیگیری — مشاوره و متقاضی بدون درخواست"
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                عملکرد کارشناسان روی درخواست‌های مشاوره و مشتریان ثبت‌نام‌شده بدون
                درخواست پروژه
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={onGetReportData}
                  disabled={loading || filterLoading}
                >
                  جستجو
                  {loading && (
                    <CircularProgress color="primary" size={16} sx={{ ml: 2 }} />
                  )}
                </Button>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<HighlightOffIcon />}
                  onClick={onClearFilterValues}
                >
                  پاک‌سازی فیلترها
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <JalaliDatePicker
                  label="از تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.fromDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({
                      ...old,
                      fromDate: value || "",
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <JalaliDatePicker
                  label="تا تاریخ"
                  fullWidth
                  size="small"
                  value={filterValues.toDate}
                  onChange={(value) =>
                    setFilterValues((old) => ({
                      ...old,
                      toDate: value || "",
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>کارشناس</InputLabel>
                  <Select
                    value={filterValues.userId}
                    label="کارشناس"
                    onChange={(e) =>
                      setFilterValues((old) => ({
                        ...old,
                        userId: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">همه</MenuItem>
                    {filterOptions.experts.map((item) => (
                      <MenuItem
                        key={item.id || item.Id}
                        value={String(item.id || item.Id)}
                      >
                        {item.fullName || item.FullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>گروه مشتری</InputLabel>
                  <Select
                    value={filterValues.source}
                    label="گروه مشتری"
                    onChange={(e) =>
                      setFilterValues((old) => ({
                        ...old,
                        source: e.target.value,
                      }))
                    }
                  >
                    {(filterOptions.sources.length
                      ? filterOptions.sources
                      : [
                          { id: 0, title: "همه" },
                          { id: 1, title: "درخواست مشاوره" },
                          { id: 2, title: "متقاضی بدون درخواست" },
                        ]
                    ).map((item) => (
                      <MenuItem
                        key={item.id ?? item.Id}
                        value={String(item.id ?? item.Id)}
                      >
                        {item.title || item.Title || item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mb: 1 }}>
              <Chip
                size="small"
                label="پیش‌فرض: ۳۰ روز اخیر — نتیجه مثبت مشاوره = تبدیل به کاربر، بدون درخواست = قصد ثبت دارد"
                variant="outlined"
              />
            </Box>

            <EnhancedTable
              rowKey="rowKey"
              rows={tableRows}
              headCells={headCells}
              pageSize={20}
              showFilter
              defaultSort={{ orderBy: "totalFollowUps", order: "desc" }}
              getRowStatus={(row) =>
                row.overdueFollowUpCount > 0 ? "error" : ""
              }
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default LeadConsultationFollowUpPerformanceReportPage;
